import { useState } from "react";
import { Link } from "react-router-dom";
import { useRegister } from "../hooks/useApi";
import Button from "../components/Button";
import OtpAuth from "../components/OtpAuth";

function getPasswordErrors(pw: string): string[] {
  const errors: string[] = [];
  if (pw.length < 8) errors.push("حداقل ۸ کاراکتر");
  if (!/[a-z]/.test(pw)) errors.push("حداقل یک حرف کوچک (a-z)");
  if (!/[A-Z]/.test(pw)) errors.push("حداقل یک حرف بزرگ (A-Z)");
  if (!/[0-9]/.test(pw)) errors.push("حداقل یک عدد (۰-۹)");
  return errors;
}

function PasswordRequirement({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-1.5 text-xs transition-colors ${ok ? "text-primary" : "text-text-tertiary"}`}>
      {ok ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      )}
      {label}
    </li>
  );
}

function extractRegisterDetail(err: unknown): string | null {
  const axiosErr = err as Record<string, unknown>;
  const resp = axiosErr?.response as Record<string, unknown> | undefined;
  const payload = (resp?.data ?? axiosErr?.data) as Record<string, unknown> | undefined;
  if (!payload) {
    const msg = (axiosErr as { message?: string })?.message;
    return msg ? String(msg) : null;
  }
  const raw = (payload as Record<string, unknown>).detail;
  if (typeof raw === "string" && raw.trim()) return raw.replace(/^Value error,\s*/i, "").trim();
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0] as Record<string, unknown>;
    const m = (first.msg as string) || (first.message as string) || "";
    if (m) return m.replace(/^Value error,\s*/i, "").trim();
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (typeof o.message === "string") return o.message;
  }
  if (typeof (payload as Record<string, unknown>).message === "string") return (payload as Record<string, unknown>).message as string;
  return null;
}

type Tab = "password" | "otp";

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tab, setTab] = useState<Tab>("otp");
  const register = useRegister();

  const passwordErrors = password ? getPasswordErrors(password) : [];
  const isPasswordValid = passwordErrors.length === 0 && password.length > 0;
  const confirmPasswordError =
    confirmPassword && password !== confirmPassword ? "رمز عبور و تکرار آن یکسان نیستند" : "";
  const isConfirmValid = !confirmPasswordError && confirmPassword.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid) return;
    if (password !== confirmPassword) return;
    register.mutate({ email, password });
  };

  const serverError = register.isError ? extractRegisterDetail(register.error) : null;

  return (
    <div className="card p-8">
      <div className="text-center mb-6">
        <img src="/logo-reverse-filled.svg" alt="ASHA-AI" className="w-12 h-12 mx-auto mb-4" />
        <h1 className="text-h1 text-text-main mb-1">ثبت نام</h1>
        <p className="text-body text-text-secondary">ایجاد حساب کاربری جدید</p>
      </div>

      <div className="flex p-1 mb-6 bg-surface-base rounded-btn border border-line">
        <button
          type="button"
          onClick={() => setTab("password")}
          className={`flex-1 py-2 px-3 rounded-btn text-small font-medium transition-all ${
            tab === "password" ? "bg-surface shadow-sm text-text-main border border-line" : "text-text-tertiary hover:text-text-secondary"
          }`}
        >
          ایمیل و رمز عبور
        </button>
        <button
          type="button"
          onClick={() => setTab("otp")}
          className={`flex-1 py-2 px-3 rounded-btn text-small font-medium transition-all ${
            tab === "otp" ? "bg-surface shadow-sm text-text-main border border-line" : "text-text-tertiary hover:text-text-secondary"
          }`}
        >
          ثبت نام با کد یکبارمصرف
        </button>
      </div>

      {tab === "password" ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">ایمیل</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 px-3.5 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              placeholder="example@email.com"
              required
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">رمز عبور</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full h-10 pl-9 pr-3.5 rounded-btn border text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all ${
                  password && !isPasswordValid ? "border-status-error-text" : "border-line"
                }`}
                placeholder="••••••••"
                required
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            {password && (
              <ul className="mt-2 space-y-1 pr-1">
                <PasswordRequirement ok={password.length >= 8} label="حداقل ۸ کاراکتر" />
                <PasswordRequirement ok={/[a-z]/.test(password)} label="حداقل یک حرف کوچک (a-z)" />
                <PasswordRequirement ok={/[A-Z]/.test(password)} label="حداقل یک حرف بزرگ (A-Z)" />
                <PasswordRequirement ok={/[0-9]/.test(password)} label="حداقل یک عدد (۰-۹)" />
              </ul>
            )}

            {register.isError && serverError && (
              <p className="text-xs text-status-error-text mt-1.5">{serverError}</p>
            )}
            {register.isError && !serverError && (
              <p className="text-xs text-status-error-text mt-1.5">خطا در ثبت نام</p>
            )}
          </div>

          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">تکرار رمز عبور</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full h-10 pl-9 pr-3.5 rounded-btn border text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all ${
                  confirmPasswordError ? "border-status-error-text" : "border-line"
                }`}
                placeholder="••••••••"
                required
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
                tabIndex={-1}
                aria-label={showConfirmPassword ? "مخفی کردن رمز عبور" : "نمایش رمز عبور"}
              >
                {showConfirmPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {confirmPasswordError && <p className="text-xs text-status-error-text mt-1.5">{confirmPasswordError}</p>}
            {isConfirmValid && !confirmPasswordError && (
              <p className="text-xs text-primary mt-1.5 flex items-center gap-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                رمز عبور مطابقت دارد
              </p>
            )}
          </div>

          <Button type="submit" className="w-full !py-2.5" loading={register.isPending} disabled={(!!password && !isPasswordValid) || !!confirmPasswordError || !isConfirmValid}>
            ثبت نام
          </Button>
        </form>
      ) : (
        <OtpAuth submitLabel="تایید و ورود به پنل" mode="signup" />
      )}

      <p className="text-center text-small text-text-tertiary mt-6">
        حساب کاربری دارید؟{" "}
        <Link to="/login" className="text-primary hover:underline font-medium">
          ورود
        </Link>
      </p>
    </div>
  );
}
